import { Language } from '../../Domain/Account/Language';

export type CampaignPersistOptions = {
  persist?: boolean;
};

export type CampaignContext = {
  code: string;
  language?: Language | null;
};

const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 30; // 30 days
const CODE_COOKIE_KEY = 'demoExperienceCode';
const LANGUAGE_COOKIE_KEY = 'demoExperienceLanguage';

/**
 * Current campaign context - can be set by URL parameter or cookie
 */
let currentCampaignCode: string | null = null;
let currentCampaignLanguage: Language | null = null;

const writeCookie = (key: string, value: string): void => {
  document.cookie = `${key}=${value};path=/;max-age=${COOKIE_MAX_AGE_SECONDS}`;
};

const readCookie = (key: string): string | null => {
  return (
    document.cookie
      .split('; ')
      .find((row) => row.startsWith(`${key}=`))
      ?.split('=')[1] ?? null
  );
};

const parseLanguage = (value: string | null): Language | null => {
  if (value === null || value === '') {
    return null;
  }

  const numericValue = Number(value);
  return Number.isInteger(numericValue) ? (numericValue as Language) : null;
};

const updateLanguageState = (
  language: Language | null,
  shouldPersist: boolean
): void => {
  currentCampaignLanguage = language;
  if (!shouldPersist) {
    return;
  }
  writeCookie(LANGUAGE_COOKIE_KEY, language === null ? '' : String(language));
};

/**
 * Set the current campaign context (code + optional language).
 * @param context Campaign context to set
 * @param options Controls whether the context should persist across sessions
 */
export function setCurrentCampaignContext(
  context: CampaignContext,
  options?: CampaignPersistOptions
): void {
  currentCampaignCode = context.code;

  const hasLanguageProp = Object.prototype.hasOwnProperty.call(context, 'language');
  const shouldPersist = options?.persist !== false;

  if (hasLanguageProp) {
    updateLanguageState(context.language ?? null, shouldPersist);
  }

  if (!shouldPersist) {
    return;
  }

  writeCookie(CODE_COOKIE_KEY, context.code);
}

/**
 * Backwards-compatible helper to set only the campaign code.
 */
export function setCurrentCampaignCode(
  code: string,
  options?: CampaignPersistOptions
): void {
  setCurrentCampaignContext({ code }, options);
}

/**
 * Get the current campaign code.
 * @returns Active campaign code or null if none is set
 */
export function getCurrentCampaignCode(): string | null {
  return currentCampaignCode;
}

/**
 * Get the current campaign language override.
 */
export function getCurrentCampaignLanguage(): Language | null {
  return currentCampaignLanguage;
}

/**
 * Initialize campaign context from cookies if available.
 */
export function initDemoExperienceFromCookie(): void {
  try {
    const code = readCookie(CODE_COOKIE_KEY);
    const language = parseLanguage(readCookie(LANGUAGE_COOKIE_KEY));

    if (code) {
      currentCampaignCode = code;
    }
    if (language !== undefined) {
      currentCampaignLanguage = language ?? null;
    }
  } catch (e) {
    console.warn('Failed to read demo experience cookie', e);
  }
} 
